import React, { useState } from 'react';
import { User, Mail, Phone, Building2, Calendar, Clock, MapPin, FileText, Video, ArrowRight } from 'lucide-react';
import investorApi from '../api/investorApi';

const Investors = () => {
  const [activeTab, setActiveTab] = useState('site-visit');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [siteVisitForm, setSiteVisitForm] = useState({
    fullName: '', email: '', phone: '', company: '', sector: '', preferredDate: '', additionalRequirements: ''
  });

  const [landAllotmentForm, setLandAllotmentForm] = useState({
    authorizedName: '', companyName: '', email: '', phone: '', industrySector: '', projectDetails: ''
  });

  const [virtualMeetingForm, setVirtualMeetingForm] = useState({
    fullName: '', email: '', phone: '', company: '', sector: '', preferredDate: '', preferredTime: '', topics: ''
  });

  const handleTabChange = (tab) => setActiveTab(tab);

  const handleSiteVisitSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await investorApi.submitSiteVisitRequest(siteVisitForm);
      if (response.success) {
        alert('Site visit request submitted successfully!');
        setSiteVisitForm({ fullName: '', email: '', phone: '', company: '', sector: '', preferredDate: '', additionalRequirements: '' });
      } else {
        alert(response.errors?.join('\n') || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Site visit submission error:', error);
      alert(error.errors?.join('\n') || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLandAllotmentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await investorApi.submitLandAllotmentRequest(landAllotmentForm);
      if (response.success) {
        alert('Land allotment application submitted successfully!');
        setLandAllotmentForm({ authorizedName: '', companyName: '', email: '', phone: '', industrySector: '', projectDetails: '' });
      } else {
        alert(response.errors?.join('\n') || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Land allotment submission error:', error);
      alert(error.errors?.join('\n') || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVirtualMeetingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await investorApi.submitVirtualMeetingRequest(virtualMeetingForm);
      if (response.success) {
        alert('Virtual meeting scheduled successfully!');
        setVirtualMeetingForm({ fullName: '', email: '', phone: '', company: '', sector: '', preferredDate: '', preferredTime: '', topics: '' });
      } else {
        alert(response.errors?.join('\n') || 'Failed to schedule meeting');
      }
    } catch (error) {
      console.error('Virtual meeting submission error:', error);
      alert(error.errors?.join('\n') || 'Failed to schedule meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Increased font sizes
  const labelStyle = "investor-form-label text-2xl font-medium text-gray-700 mb-2 block text-left";
  const inputStyle = "w-full px-12 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#672f89]/20 focus:border-[#672f89] transition-all duration-200 text-gray-900 //placeholder-gray-500";

  return (
    <div style={{ fontFamily: "'Rubik', sans-serif", minHeight: '100vh', backgroundColor: '#f9fafb', padding: '40px 20px', marginTop: "20px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap');

        @media (max-width: 768px) {
          .investor-page-title {
            font-size: 26px !important;
          }
          .investor-page-subtitle {
            font-size: 14px !important;
          }
          .investor-form-grid {
            grid-template-columns: 1fr !important;
          }
          .investor-form-label {
            font-size: 14px !important;
          }
          .investor-form-header {
            padding: 24px 20px 16px !important;
          }
          .investor-form-body {
            padding: 20px !important;
          }
        }
      `}</style>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Page Heading */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <h1 className="investor-page-title" style={{ fontSize: '34px', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '8px' }}>
            Investors
          </h1>
          <p className="investor-page-subtitle" style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
            Schedule a site visit or virtual meeting with our investment facilitation team
          </p>
        </div>

        {/* Tab Navigation - Clean Design */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '40px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {[
            { id: 'site-visit', label: 'Site Visit', icon: <MapPin size={20} /> },
            // { id: 'land-allotment', label: 'Land Allotment', icon: <FileText size={20} /> },
            { id: 'virtual-meeting', label: 'Virtual Meeting', icon: <Video size={20} /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '500',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #672f89' : '2px solid transparent',
                color: activeTab === tab.id ? '#672f89' : '#6b7280',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: "'Rubik', sans-serif"
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Container */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', 
          overflow: 'hidden' 
        }}>
          
          {/* Form Header */}
          <div className="investor-form-header" style={{ padding: '36px 36px 20px 36px', borderBottom: '1px solid #f3f4f6' }}>
            <h2 style={{ fontSize: '26px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
              {activeTab === 'site-visit' && 'Schedule Site Visit'}
              {/* {activeTab === 'land-allotment' && 'Land Allotment Application'} */}
              {activeTab === 'virtual-meeting' && 'Schedule Virtual Meeting'}
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280' }}>
              {activeTab === 'site-visit' && ''}
              {/* {activeTab === 'land-allotment' && ''} */}
              {activeTab === 'virtual-meeting' && ''}
            </p>
          </div>

          {/* Form Content */}
          <div className="investor-form-body" style={{ padding: '32px 36px 36px 36px' }}>
            {/* Site Visit Form */}
            {activeTab === 'site-visit' && (
              <div>
                <div className="investor-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                  <div>
                    <label className={labelStyle}>Full Name</label>
                    <div style={{ position: 'relative' }}>
                      <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={siteVisitForm.fullName} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, fullName: e.target.value })} 
                        className={inputStyle}
                        ////placeholder="Enter your full name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Email Address</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="email" 
                        value={siteVisitForm.email} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, email: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter your email address"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Phone Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="tel" 
                        value={siteVisitForm.phone} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, phone: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter your phone number"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Company Name</label>
                    <div style={{ position: 'relative' }}>
                      <Building2 size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={siteVisitForm.company} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, company: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter company name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Sector of Interest</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>
                        </svg>
                      </div>
                      <select 
                        value={siteVisitForm.sector} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, sector: e.target.value })} 
                        className={inputStyle}
                        style={{ cursor: 'pointer', fontSize: '16px', paddingLeft: '48px', appearance: 'none' }}
                      >
                        <option value="">Select Industry Sector</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="technology">Technology & IT</option>
                        <option value="logistics">Logistics & Warehousing</option>
                        <option value="pharmaceutical">Pharmaceutical</option>
                        <option value="renewable">Renewable Energy</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Preferred Visit Date</label>
                    <div style={{ position: 'relative' }}>
                      {/* <Calendar size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} /> */}
                      <input 
                        type="date" 
                        value={siteVisitForm.preferredDate} 
                        onChange={(e) => setSiteVisitForm({ ...siteVisitForm, preferredDate: e.target.value })} 
                        className={inputStyle}
                        style={{ fontSize: '16px', paddingLeft: '48px', color: siteVisitForm.preferredDate ? '#111827' : '#9ca3af' }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '32px' }}>
                  <label className={labelStyle}>Additional Requirements</label>
                  <textarea 
                    value={siteVisitForm.additionalRequirements} 
                    onChange={(e) => setSiteVisitForm({ ...siteVisitForm, additionalRequirements: e.target.value })} 
                    className={inputStyle}
                    //placeholder="Enter any additional requirements or special requests"
                    style={{ minHeight: '100px', resize: 'vertical', fontSize: '16px', padding: '12px 16px', paddingLeft: '16px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button 
                    onClick={handleSiteVisitSubmit}
                    disabled={isSubmitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 32px',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isSubmitting ? '#9ca3af' : '#672f89',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => !isSubmitting && (e.target.style.background = '#7c3a9f')}
                    onMouseLeave={(e) => !isSubmitting && (e.target.style.background = '#672f89')}
                  >
                    {isSubmitting ? 'Processing...' : 'Submit Request'}
                    {!isSubmitting && <ArrowRight size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Land Allotment Form */}
            {activeTab === 'land-allotment' && (
              <div>
                <div className="investor-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                  <div>
                    <label className={labelStyle}>Authorized Representative</label>
                    <div style={{ position: 'relative' }}>
                      <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={landAllotmentForm.authorizedName} 
                        onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, authorizedName: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter authorized representative name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Company Name</label>
                    <div style={{ position: 'relative' }}>
                      <Building2 size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={landAllotmentForm.companyName} 
                        onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, companyName: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter company name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Official Email</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="email" 
                        value={landAllotmentForm.email} 
                        onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, email: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter official email address"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Contact Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="tel" 
                        value={landAllotmentForm.phone} 
                        onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, phone: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter contact number"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className={labelStyle}>Industry Sector</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/>
                        </svg>
                      </div>
                      <select 
                        value={landAllotmentForm.industrySector} 
                        onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, industrySector: e.target.value })} 
                        className={inputStyle}
                        style={{ cursor: 'pointer', fontSize: '16px', paddingLeft: '48px', appearance: 'none' }}
                      >
                        <option value="">Select Industry Sector</option>
                        <option value="heavy-manufacturing">Heavy Manufacturing</option>
                        <option value="light-assembly">Light Assembly</option>
                        <option value="logistics-hub">Logistics Hub</option>
                        <option value="rd-center">Research & Development</option>
                        <option value="clean-energy">Clean Energy</option>
                        <option value="other">Other Specialized</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '32px' }}>
                  <label className={labelStyle}>Project Details</label>
                  <textarea 
                    value={landAllotmentForm.projectDetails} 
                    onChange={(e) => setLandAllotmentForm({ ...landAllotmentForm, projectDetails: e.target.value })} 
                    className={inputStyle}
                    //placeholder="Describe your project details, requirements, and objectives"
                    style={{ minHeight: '120px', resize: 'vertical', fontSize: '16px', padding: '12px 16px', paddingLeft: '16px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button 
                    onClick={handleLandAllotmentSubmit}
                    disabled={isSubmitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 32px',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isSubmitting ? '#9ca3af' : '#672f89',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => !isSubmitting && (e.target.style.background = '#7c3a9f')}
                    onMouseLeave={(e) => !isSubmitting && (e.target.style.background = '#672f89')}
                  >
                    {isSubmitting ? 'Processing...' : 'Submit Application'}
                    {!isSubmitting && <ArrowRight size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Virtual Meeting Form */}
            {activeTab === 'virtual-meeting' && (
              <div>
                <div className="investor-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                  <div>
                    <label className={labelStyle}>Full Name</label>
                    <div style={{ position: 'relative' }}>
                      <User size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={virtualMeetingForm.fullName} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, fullName: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter your full name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Email Address</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="email" 
                        value={virtualMeetingForm.email} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, email: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter your email address"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Phone Number</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="tel" 
                        value={virtualMeetingForm.phone} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, phone: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter your phone number"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Company Name</label>
                    <div style={{ position: 'relative' }}>
                      <Building2 size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <input 
                        type="text" 
                        value={virtualMeetingForm.company} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, company: e.target.value })} 
                        className={inputStyle}
                        //placeholder="Enter company name"
                        style={{ fontSize: '16px', paddingLeft: '48px' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Preferred Date</label>
                    <div style={{ position: 'relative' }}>
                      {/* <Calendar size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} /> */}
                      <input 
                        type="date" 
                        value={virtualMeetingForm.preferredDate} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, preferredDate: e.target.value })} 
                        className={inputStyle}
                        style={{ fontSize: '16px', paddingLeft: '48px', color: virtualMeetingForm.preferredDate ? '#111827' : '#9ca3af' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelStyle}>Preferred Time</label>
                    <div style={{ position: 'relative' }}>
                      <Clock size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', zIndex: 10 }} />
                      <select 
                        value={virtualMeetingForm.preferredTime} 
                        onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, preferredTime: e.target.value })} 
                        className={inputStyle}
                        style={{ paddingLeft: '48px', cursor: 'pointer', fontSize: '16px', appearance: 'none' }}
                      >
                        <option value="">Select Time Slot</option>
                        <option value="9:00 AM">09:00 AM - 10:00 AM</option>
                        <option value="10:30 AM">10:30 AM - 11:30 AM</option>
                        <option value="1:00 PM">01:00 PM - 02:00 PM</option>
                        <option value="2:30 PM">02:30 PM - 03:30 PM</option>
                        <option value="4:00 PM">04:00 PM - 05:00 PM</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '32px' }}>
                  <label className={labelStyle}>Discussion Topics</label>
                  <textarea 
                    value={virtualMeetingForm.topics} 
                    onChange={(e) => setVirtualMeetingForm({ ...virtualMeetingForm, topics: e.target.value })} 
                    className={inputStyle}
                    //placeholder="Enter topics you'd like to discuss during the meeting"
                    style={{ minHeight: '100px', resize: 'vertical', fontSize: '16px', padding: '12px 16px', paddingLeft: '16px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button 
                    onClick={handleVirtualMeetingSubmit}
                    disabled={isSubmitting}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 32px',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isSubmitting ? '#9ca3af' : '#672f89',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      fontFamily: "'Rubik', sans-serif"
                    }}
                    onMouseEnter={(e) => !isSubmitting && (e.target.style.background = '#7c3a9f')}
                    onMouseLeave={(e) => !isSubmitting && (e.target.style.background = '#672f89')}
                  >
                    {isSubmitting ? 'Processing...' : 'Schedule Meeting'}
                    {!isSubmitting && <ArrowRight size={18} />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Investors;